import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { updateMePassword } from "../../Redux/Thunks/AuthThunks";
import { logout } from "../../Redux/Slices/authSlice";
import UseTitle from "../../Utility/UseTitle/UseTitle";
import Typography from "../../Utility/Typography";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Label from "../../Utility/Label/Label";
import ErrorMsg from "../../Utility/ErrorMsg";
import PasswordInput from "../../Utility/PasswordToggle/PassswordInput";
import { ROUTES } from "../../constants";
import { logger } from "../../utils/logger";
import logoImage from "../../assets/images/logoNew.jpg";

const schema = yup.object().shape({
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      "Password must contain uppercase, lowercase, number and special character"
    )
    .required("New password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Please confirm your password"),
});

export default function ForcePassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const authToken = JSON.parse(localStorage.getItem("authToken") || "null");
    if (!authToken?.accessToken) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onSubmit = async (data) => {
    setSubmitError("");
    setIsSubmitting(true);
    try {
      await dispatch(updateMePassword(data.password)).unwrap();
      dispatch(logout());
      localStorage.removeItem("authToken");
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (err) {
      logger.error("Force password change failed:", err);
      setSubmitError(err?.message || "Failed to update password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <UseTitle formattedTitle="Set New Password" />
      <div className="auth-page-wrapper ">
        <div className="auth-page-content pb-0">
          <div className="w-full max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-screen">
              <div className="w-full md:w-2/3 lg:w-1/2 xl:w-2/5">
                <div className="bg-surface-primary rounded-md border border-border-primary shadow-sm mt-4">
                  <div className="mt-5">
                    <div className="flex justify-center items-center">
                      <img
                        src={logoImage}
                        alt="Logo"
                        className="h-[72px] w-auto max-w-[280px] object-contain"
                      />
                    </div>
                  </div>
                  <div className="p-4 mt-4">
                    <Typography variant="h4" className="text-center mb-2 text-theme-primary">
                      Set your password
                    </Typography>
                    <Typography variant="text2" className="text-center text-text-secondary mb-4">
                      You must set a new password before continuing.
                    </Typography>
                    {submitError && (
                      <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                        {submitError}
                      </div>
                    )}
                    <form onSubmit={handleSubmit(onSubmit)} className="p-2 space-y-4">
                      <div>
                        <Label htmlFor="force-password" required>
                          New password
                        </Label>
                        <PasswordInput
                          register={register}
                          name="password"
                          id="force-password"
                          placeholder="Enter new password"
                        />
                        <ErrorMsg message={errors.password?.message} className="mt-1" />
                      </div>
                      <div>
                        <Label htmlFor="force-confirm" required>
                          Confirm password
                        </Label>
                        <PasswordInput
                          register={register}
                          name="confirmPassword"
                          id="force-confirm"
                          placeholder="Confirm new password"
                        />
                        <ErrorMsg message={errors.confirmPassword?.message} className="mt-1" />
                      </div>
                      <FormSubmitButton
                        loading={isSubmitting}
                        label="Set password and continue"
                        loadingText="Saving..."
                        idleIconClass=""
                        className="w-full flex items-center justify-center gap-2"
                      />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
